/**
 * Тексты модуля фильтров, сортировки и представлений (§10, §4.5).
 *
 * Правило спеки §12: ни одной строки текста в компонентах. Общий словарь
 * `web/src/texts.ts` читают сразу несколько волн, поэтому свои строки модуль
 * держит здесь, а склонение числительных берёт из общего файла.
 */
import { countOf, forms, plural } from '../../../texts.js'

export const filterTexts = {
  /** §4.1 строка управления */
  bar: {
    label: 'Управление таблицей',
    channelCaption: (n: number) => countOf(n, forms.brand),
    sortLabel: 'Сортировка',
    densityLabel: 'Формат отображения',
    addBrand: 'Бренд',
    ask: 'Спросить',
    /* Глифы ⚙ ⚗ ⧉ из §12.1 рисуются иконками: в Aeonik Pro их нет. */
    columns: 'Колонки',
    columnsLabel: 'Выбор колонок',
    columnsCustom: 'Показаны не все колонки · вернуть',
    filters: (n: number) => (n > 0 ? `Фильтры (${n})` : 'Фильтры'),
    filtersLabel: 'Фильтры',
    export: 'Выгрузить',
    exportLabel: 'Выгрузка таблицы',
    exportXlsx: 'Таблица XLSX',
    exportXlsxHint: 'Все бренды обоих каналов · Excel, Google Таблицы',
    exportCsv: 'CSV с точкой с запятой',
    exportCsvHint: 'Все бренды обоих каналов · для импорта в другие системы',
  },

  /** §10.3 пресеты сортировки */
  sort: {
    alphabet: 'А→Я',
    priority: 'По приоритету',
    recent: 'Недавно изменённые',
    unfilled: 'Сначала незаполненные',
    custom: 'Своя сортировка',
    reset: 'Сбросить',
    emptiesLast: 'Пустые — в конце',
    level: (n: number) => `уровень ${n}`,
    ascLabel: 'по возрастанию',
    descLabel: 'по убыванию',
  },

  /** §4.5 счётчики и ссылка на вид */
  counters: {
    shown: 'Показано',
    of: 'из',
    shownLabel: (shown: number, total: number) =>
      `Показано ${shown} ${plural(shown, forms.brand)} из ${total}`,
    copyLink: 'Ссылка на этот вид',
    copyLinkLabel: 'Скопировать ссылку на этот вид',
    copied: 'Ссылка скопирована',
    copyFailed: 'Не удалось скопировать ссылку',
  },

  /** §10.1 алгебра */
  algebra: {
    hint: 'Внутри группы — или, между группами — и',
    excluded: 'исключён',
  },

  /** §10.2 чипы строки чипов */
  chips: {
    label: 'Быстрые фильтры',
    tierGroup: 'Тир',
    tierEmpty: '—',
    tierEmptyLabel: 'без тира',
    noUrl: 'Без URL',
    /* Та же пометка, что и в карточке: «нет категорий» читалось как «категории
       не привязаны», а это другое — у бренда их может быть шесть. */
    unclear: 'Уточнить категории',
    task: 'Есть задача',
    taskLabel: 'Есть незакрытая задача',
    commented: (days: number) => `Комментарий за ${days} ${plural(days, ['день', 'дня', 'дней'])}`,
    unclearLabel: 'Категории не уточнены',
    hidden: 'Скрыт',
    hiddenLabel: 'Скрыт с сайта',
    comments: '💬',
    commentsLabel: 'Есть комментарии',
    changed: (days: number) => `Изменён ${days} дн`,
    changedLabel: 'Изменён за последние дни',
    changedPeriod: 'Период изменения',
    changedDays: (days: number) => `за ${days} ${plural(days, ['день', 'дня', 'дней'])}`,
    lens: 'Изменилось с прошлого визита',
    mine: 'Только мои правки',
    more: '+',
    moreLabel: 'Все фильтры',
  },

  /** Полоса фильтров: названия групп и быстрые условия. */
  pillGroups: {
    title: 'Фильтры',
    quick: 'Быстрые фильтры',
    tier: 'Тир',
    visibility: 'Видимость',
    seo: 'Приоритет SEO',
    context: 'Приоритет Контекст',
    empty: 'Не задан',
    noTier: 'Без тира',
    hidden: 'Скрытые',
    comments: 'С комментариями',
    noSeo: 'Без приоритета SEO',
    noContext: 'Без приоритета контекста',
    clearOne: (group: string) => `Снять фильтр «${group}»`,
  },

  /** §10.2 строка активных пилюль */
  pills: {
    label: 'Активные фильтры',
    prefix: '⌁',
    remove: (name: string) => `Снять фильтр: ${name}`,
    clearAll: 'Очистить всё',
    /** §5.4 снимок разошёлся с фильтром */
    stale: (n: number) => `${countOf(n, forms.row)} больше не подходят под фильтр`,
    refresh: 'Обновить',
  },

  /** §10.2 панель «+ Фильтр» */
  panel: {
    title: 'Фильтры',
    blockSite: 'На сайте',
    blockPriority: 'Приоритет',
    blockPromo: 'Продвижение',
    blockService: 'Сервис White Glove и заметки',
    blockOther: 'Прочее',
    blockFields: 'Поля',
    unset: 'не задан',
    unsetTier: 'без тира',
    any: 'любой',
    reset: 'Сбросить фильтры',
    close: 'Закрыть',

    presentIn: 'Канал присутствия',
    presentRetail: 'Есть в Рознице',
    presentB2b: 'Есть в Опте',
    presentOnlyOne: 'Только в одном канале',
    presentBoth: 'В обоих каналах',

    visibility: 'Видимость на сайте',
    url: 'Адрес страницы',
    urlAny: 'Любой',
    urlFilled: 'Заполнен',
    urlEmpty: 'Не заполнен',
    urlHiddenInB2b: 'В Опте адрес не заполнен ни у одного бренда',
    stockDisplay: 'Отображаемый статус наличия',
    oos: 'Статус «нет в наличии»',

    tier: 'Приоритет (тир)',
    categories: 'Категория',
    categoriesSearch: 'Поиск по справочнику',
    categoriesMode: 'Как сопоставлять',
    categoriesAny: 'любая из',
    categoriesAll: 'все выбранные',
    categoriesEmpty: 'Ничего не нашлось',
    categoriesSelected: (n: number) => `выбрано ${n}`,
    unclear: 'Категории не уточнены',

    seo: 'Приоритет SEO',
    context: 'Приоритет Контекст',

    whiteGlove: 'White Glove заполнен',
    wgDelivery: 'Доставка',
    wgInstallation: 'Установка',
    wgWarranty: 'Гарантия',

    comments: 'Есть комментарии',
    channelDiff: 'Расхождения каналов (строгие)',
    channelDiffHint: 'Оба значения заполнены и различны',
    onlyOneChannel: 'Заполнено только в одном канале',
    changedWithin: 'Изменён за',
    changedAny: 'любое время',
    changedDays: (days: number) => `${days} ${plural(days, ['день', 'дня', 'дней'])}`,
    changedBy: 'Автор последнего изменения',
    changedBySystem: 'Импорт из Google-таблицы',
    commentBy: 'Автор последнего комментария',
    commentWithin: 'Комментарий за',
    hasTask: 'Незакрытая задача',
    status: 'Статус бренда',
    statusActive: 'Активные',
    statusArchived: 'Архив',
    statusAll: 'Все',
    onlyMine: 'Только мои правки',

    fieldFilled: 'Заполнено',
    fieldEmpty: 'Пусто',
    fieldYes: 'Да',
    fieldNo: 'Нет',
    fieldFrom: 'от',
    fieldTo: 'до',
    fieldsEmpty: 'Пользовательских полей пока нет',
  },

  /** §10.4 представления */
  views: {
    label: 'Представления',
    all: 'Все бренды',
    more: '+',
    moreLabel: 'Все представления',
    menuLabel: 'Представления',
    shared: 'Общие',
    personal: 'Личные',
    modes: 'Персональные режимы',
    sharedBadge: 'общее',
    personalBadge: 'личное',
    overridden: 'Вид изменён',
    save: 'Сохранить вид',
    rename: 'Переименовать',
    remove: 'Удалить',
    removeConfirm: (name: string) => `Удалить представление «${name}»?`,
    removeConsequence: 'Его больше не увидит никто. Фильтры при этом останутся на экране.',
    removeSubmit: 'Удалить',

    dialogTitle: 'Сохранить представление',
    dialogRename: 'Переименовать представление',
    name: 'Название',
    namePlaceholder: 'A+ в рознице без URL',
    nameRequired: 'Поле обязательно к заполнению',
    whatSaved: 'Что сохраняется:',
    savedChannel: 'Канал',
    savedFilters: 'Фильтры',
    savedColumns: 'Колонки',
    savedSort: 'Сортировка',
    savedNoFilters: 'без фильтров',
    savedDefaultColumns: 'набор по умолчанию',
    share: 'Показать всем',
    shareHint: 'доступно собственнику и коммерческому директору',
    shareDenied: 'Общие представления создаёт коммерческий директор или собственник',
    submit: 'Сохранить',
    cancel: 'Отмена',

    created: (name: string) => `Представление «${name}» сохранено`,
    renamed: 'Название изменено',
    removed: (name: string) => `Представление «${name}» удалено`,

    /** §10.4 стартовый набор и персональные режимы */
    todo: 'Доделки',
    todoHint: 'Без URL или категории не уточнены',
    myMissing: 'Мои пропуски',
    myMissingHint: 'Пустые поля из вашей зоны ответственности',
    seoUnset: 'SEO не задан',
    contextUnset: 'Контекст не задан',
    tierUnset: 'Тир не задан',
    hiddenFromSite: 'Скрыты с сайта',
    compare: 'Сравнить каналы',
    apANoUrl: 'A+ и A без URL',
    myTiers: 'Мои приоритеты (тир)',
    mySeo: 'Мои приоритеты SEO',
    myContext: 'Мои приоритеты Контекст',
    mySite: 'Мои доделки по сайту',
  },

  /** подписи групп фильтров для пилюль */
  group: {
    query: 'Поиск',
    tiers: 'Тир',
    presentIn: 'Канал присутствия',
    visibility: 'Видимость',
    categoryIds: 'Категория',
    seoPriority: 'SEO',
    contextPriority: 'Контекст',
    stockDisplay: 'Статус наличия',
    oos: 'Нет в наличии',
    hasUrl: 'URL',
    categoriesUnclear: 'Категории не уточнены',
    hasComments: 'Комментарии',
    commentBy: 'Автор последнего комментария',
    commentWithinDays: 'Комментарий за',
    hasTask: 'Незакрытая задача',
    changedWithinDays: 'Изменён за',
    changedBy: 'Автор',
    status: 'Статус',
    whiteGlove: 'White Glove',
    channelDiff: 'Расхождения каналов',
    onlyOneChannel: 'Заполнено в одном канале',
    onlyMine: 'Мои правки',
    lensVisit: 'С прошлого визита',
    todo: 'Доделки',
    myMissing: 'Мои пропуски',
    field: (name: string) => name,
  },
} as const
